import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button.js';
import { Money } from '@/components/money/Money.js';
import { useFormatMoney } from '@/components/money/MoneyProvider.js';
import { mapApiError, ApiError } from '@/lib/api/error.js';
import { invalidateClaimState } from '@/features/promo/api.js';
import { prizeLabel } from '@/features/spin/prize-label.js';
import { landingIndex, landingRotation } from '@/features/spin/landing.js';
import { SpinWheel, WHEEL_COLORS } from './SpinWheel.js';
import { SpinResultModal } from './SpinResultModal.js';
import {
  fetchSpinEvent,
  doSpin,
  SPIN_QUERY_KEYS,
  type SpinEligibility,
  type SpinPrize,
  type SpinResult,
} from '@/features/spin/api.js';

function PrizeValue({ prize, currency }: { prize: SpinPrize; currency: string }) {
  const { t } = useTranslation('spin');
  if (prize.cash && prize.cash > 0) return <Money minor={prize.cash} currency={currency} />;
  return <span className="text-[var(--text-muted)]">{t('result.nothing')}</span>;
}

/**
 * Say which requirement is holding the wheel and what it asks for.
 *
 * `required` is the server's digit string, and its unit depends on the gate:
 * the two money gates carry MINOR UNITS, which have to go through `formatMoney`
 * with the brand's currency — dividing by 100 here would print a JPY threshold
 * a hundred times too small (#426). `minDepositCount` is a plain count and must
 * never reach the money formatter.
 *
 * The shortfall is deliberately not computed: the reply carries the threshold
 * only, so any "you still need X" here would be an invented number.
 */
function gateNotice(
  t: (key: string, opts?: Record<string, unknown>) => string,
  eligibility: SpinEligibility | null | undefined,
  fmt: (minor: bigint) => string,
): string {
  const gate = eligibility?.gate ?? 'unknown';
  const required = eligibility?.required;
  // `required` is a CURRENCY CODE here, not digits, so this is decided before
  // the digit test below sends every non-numeric value to the "try again" line.
  if (gate === 'currency' && required) return t('gate.currency', { currency: required });
  if (gate === 'unknown' || required == null || !/^\d+$/.test(required)) return t('gate.unknown');
  if (gate === 'minDepositCount') return t('gate.minDepositCount', { times: required });
  return t(`gate.${gate}`, { amount: fmt(BigInt(required)) });
}

export function SpinPanel({ currency }: { currency: string }) {
  const { t } = useTranslation('spin');
  const formatMoney = useFormatMoney();
  const qc = useQueryClient();
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [won, setWon] = useState<SpinResult | null>(null);
  const [wonTurnover, setWonTurnover] = useState<number | null>(null);

  const eventQuery = useQuery({ queryKey: SPIN_QUERY_KEYS.event, queryFn: fetchSpinEvent, staleTime: 15_000 });

  const spinMut = useMutation({
    mutationFn: doSpin,
    onSuccess: ({ result, turnoverRequired }) => {
      const prizes = eventQuery.data?.prizes ?? [];
      // The server says WHICH wedge won by position: two wedges can carry the
      // same key on a wheel stored before PROMO-724 existed, and a key lookup
      // then stops the pointer on the wrong one (#555).
      const idx = landingIndex(prizes, result);
      setRotation((r) => landingRotation(r, prizes.length, idx));
      setSpinning(true);
      window.setTimeout(() => {
        setSpinning(false);
        setWon(result);
        setWonTurnover(turnoverRequired ?? null);
        void qc.invalidateQueries({ queryKey: SPIN_QUERY_KEYS.event });
        // A spin prize can carry a wagering requirement (`turnoverRequired`), so
        // it creates the same kind of live claim a promotion does — and the same
        // play restrictions with it.
        invalidateClaimState(qc);
      }, 4200);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'PROMO-713') return toast.info(t('errors.maxPerDay'));
      if (err instanceof ApiError && err.code === 'PROMO-715') return toast.info(t('errors.noBalance'));
      toast.error(mapApiError(err, t));
    },
  });

  const ev = eventQuery.data;
  if (!ev || !ev.available) return null;

  const prizes = ev.prizes ?? [];
  const balance = ev.balance ?? 0;
  // The entitlement a player holds is not what they may spend today: the
  // config's per-day cap is the binding number whenever it is the lower one.
  // A null cap means the config sets no daily limit at all.
  const remainingToday = ev.spinsRemainingToday ?? null;
  const spinsNow = remainingToday == null ? balance : Math.min(balance, remainingToday);
  const capBinds = remainingToday != null && remainingToday < balance;
  // Only an explicit `false` gates the button: a server that predates #298 omits
  // the field, and reading `undefined` as "blocked" would dead-end every wheel.
  const blocked = ev.eligible === false;
  const canSpin = spinsNow > 0 && !spinning && !blocked;

  const perSpin = ev.depositPerSpin ?? 0;
  const accum = ev.depositAccum ?? 0;
  const progressPct = perSpin > 0 ? Math.min(100, (accum / perSpin) * 100) : 0;
  const remainingToNext = perSpin > 0 ? Math.max(0, perSpin - accum) : 0;
  const multiplier = ev.turnoverMultiplier ?? 2;
  // How many more entitlements a deposit can earn today — a different cap from
  // the one above, which limits how many may be spent. A number is the only
  // thing worth saying: `null` is a config with no grant cap, and `undefined` a
  // server that predates the field, and neither should invent a "0 left".
  const grantsLeft = typeof ev.grantsRemainingToday === 'number' ? ev.grantsRemainingToday : null;

  return (
    <section className="rounded-2xl bg-gradient-to-b from-[var(--bg-raised)] to-[var(--bg-elevated)] p-5 flex flex-col items-center gap-4">
      {perSpin > 0 && (
        <p className="text-[12px] text-[var(--text-muted)] text-center">
          {t('earn.rule', { amount: formatMoney(perSpin, currency) })}
        </p>
      )}

      <SpinWheel prizes={prizes} rotationDeg={rotation} spinning={spinning} />

      <div className="flex flex-col items-center gap-0.5">
        <div className="text-[14px] font-semibold text-[var(--text-primary)]">
          {t('balance')}: <span className="text-[#f5c518]">{spinsNow}</span>
        </div>
        {capBinds && (
          <div className="text-[12px] text-[var(--text-muted)]">
            {t('balance.today', { today: spinsNow, total: balance })}
          </div>
        )}
      </div>

      {blocked && (
        <p
          role="status"
          className="w-full max-w-[320px] rounded-xl bg-[var(--warning-bg)] px-3 py-2 text-center text-[12.5px] leading-relaxed text-[var(--warning)]"
        >
          {gateNotice(t, ev.eligibility, (m) => formatMoney(m, currency))}
        </p>
      )}

      <Button glow disabled={!canSpin} onClick={() => spinMut.mutate()} className="min-w-[150px]">
        {spinning ? t('spinning') : t('spin')}
      </Button>

      {perSpin > 0 && (
        <div className="w-full max-w-[320px] flex flex-col items-center gap-1.5">
          <div
            className="w-full h-2 rounded-full bg-[var(--bg-elevated)] border border-[var(--border-subtle)] overflow-hidden"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progressPct)}
          >
            <div
              className="h-full rounded-full bg-[var(--primary)] transition-[width] duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="text-[12px] text-[var(--text-muted)]">
            {t('earn.progress', { amount: formatMoney(remainingToNext, currency) })}
          </span>
          {grantsLeft !== null && (
            <span className="text-[12px] text-[var(--text-muted)] text-center">
              {grantsLeft > 0 ? t('earn.grantsToday', { n: grantsLeft }) : t('earn.grantsDone')}
            </span>
          )}
          <Link to="/wallet/deposit" className="text-[12.5px] font-semibold text-[var(--primary)] hover:underline">
            {t('earn.depositCta')}
          </Link>
        </div>
      )}

      {/* prize legend — full width, two columns, always upright/readable */}
      <div className="w-full border-t border-[var(--border-subtle)] pt-3">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          {prizes.map((p, i) => (
            // Keyed by position: a prize key is not unique on a wheel stored
            // before PROMO-724, and React would drop the second row (#555).
            <div key={`${p.key}-${i}`} className="flex items-center gap-2 text-[12.5px] min-w-0">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                style={{ background: WHEEL_COLORS[i % WHEEL_COLORS.length] }}
              />
              <span className="flex-1 truncate text-[var(--text-secondary)]">{prizeLabel(p, t)}</span>
              <span className="font-semibold text-[var(--text-primary)] shrink-0">
                <PrizeValue prize={p} currency={currency} />
              </span>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11.5px] text-[var(--text-muted)] text-center">
          {t('bonus.note', { x: multiplier })}
        </p>
      </div>

      <SpinResultModal open={!!won} onClose={() => setWon(null)} result={won} currency={currency} turnoverMultiplier={multiplier} turnoverRequired={wonTurnover} />
    </section>
  );
}
